import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import clsx from 'clsx';
import styles from './OperatingInflowReconciliation.module.scss';
import {
    fetchOperatingInflowReconData,
    downloadOperatingInflowReconExport,
} from '../../../../../store/dashboard/reconciliation/reconciliationApiHelpers';
import { useParams } from 'react-router-dom';
import Header from './components/Header';
import Rows from './components/Rows';
import { ShimmerThumbnail } from 'react-shimmer-effects';
import HeaderBodyBox from '../../../../../common/HeaderBodyBox/HeaderBodyBox';
import { DatePicker } from '../../../../../common/_custom/DatePicker/DatePicker';
import CalendarIcon from '../../../../../assets/Svg/CalendarIcon';
import { noDataFoundEmptyState, infoToolTip } from '../../../../../assets/hostedassets';
import { DATE_FORMATS } from '../../../../../utils/dateUtils';
import { useAppSelector } from '../../../../../app/hooks';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { AICA_RECON_FILTER_APPLIED, AICA_RECON_EXPORT_CLICKED } from '../../../../../utils/constants/mixpanelEvents/investorEvents';
import Button from '../../../../../common/_custom/Button/Button';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { downloadIconDataRoomHovered } from '../../../../../assets/hostedassets';

const OVERLAP_TOOLTIP_TITLE = 'Overlapping data for GST and Banking is available for below duration';

interface DateRange {
    start: string;
    end: string;
}

const EMPTY_DATE_RANGE: DateRange = { start: '', end: '' };

function OperatingInflowReconciliation({ assesseeOrgId }) {
    const params = useParams();
    const orgId = assesseeOrgId || params.id;
    const user = useAppSelector((state) => state.user);

    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [tableData, setTableData] = useState<any>(null);

    const [dateRange, setDateRange] = useState<DateRange>(EMPTY_DATE_RANGE);
    const [appliedDateRange, setAppliedDateRange] = useState<DateRange>(EMPTY_DATE_RANGE);

    const defaultDateRangeRef = useRef<DateRange>(EMPTY_DATE_RANGE);

    const [overlapDurationRanges, setOverlapDurationRanges] = useState<string[]>([]);

    const isInitializedRef = useRef(false);

    // Per-picker filled state — highlight as soon as user picks a date different from default
    const isStartCustomApplied =
        !!dateRange.start && !!defaultDateRangeRef.current.start && dateRange.start !== defaultDateRangeRef.current.start;

    const isEndCustomApplied = !!dateRange.end && !!defaultDateRangeRef.current.end && dateRange.end !== defaultDateRangeRef.current.end;

    // True when either picker has a custom applied value (used for Reset button visibility)
    const isCustomFilterApplied = isStartCustomApplied || isEndCustomApplied;

    // True when picker dates differ from last applied dates (Apply button should show)
    const hasUnappliedChanges =
        isInitializedRef.current &&
        !!dateRange.start &&
        !!dateRange.end &&
        (dateRange.start !== appliedDateRange.start || dateRange.end !== appliedDateRange.end);

    useEffect(() => {
        if (!orgId) return;
        fetchData();
    }, [orgId]);

    const fetchData = (fromDate?: string, toDate?: string, shouldTrackMixpanel = false) => {
        // hasDateFilter: true means user explicitly passed dates (filter apply / reset)
        // false means initial load — backend picks default date range
        const hasDateFilter = !!fromDate && !!toDate;

        setLoading(true);
        fetchOperatingInflowReconData(
            { orgId, gstType: 'REVENUE', ...(hasDateFilter ? { startDate: fromDate, endDate: toDate } : {}) },
            {
                onSuccess: (data) => {
                    setTableData(data);

                    // Only on first load — set overlap tooltip + default dates from backend dateRange
                    if (!isInitializedRef.current) {
                        if (data?.dateRange) {
                            setOverlapDurationRanges([data.dateRange]);
                        }
                        if (data?.startDate && data?.endDate) {
                            const parsed: DateRange = { start: data.startDate, end: data.endDate };
                            setDateRange(parsed);
                            setAppliedDateRange(parsed);
                            defaultDateRangeRef.current = parsed;
                        }
                        isInitializedRef.current = true;
                    }

                    // Update applied dates when filter is explicitly applied or reset
                    if (hasDateFilter) {
                        setAppliedDateRange({ start: fromDate, end: toDate });
                    }

                    // Track Mixpanel only when user explicitly applies a filter
                    if (shouldTrackMixpanel && hasDateFilter) {
                        const days = dayjs(toDate).diff(dayjs(fromDate), 'day') + 1;
                        Mixpanel.track(AICA_RECON_FILTER_APPLIED, {
                            userEmail: user?.userData?.primaryEmail || '',
                            startDate: fromDate,
                            endDate: toDate,
                            durationInDays: days > 0 ? days : 0,
                            currentURL: window.location.href,
                            pnoID: user?.userData?.investorOrgId || '',
                        });
                    }

                    setLoading(false);
                },
                onError: () => {
                    setLoading(false);
                },
            },
        );
    };

    const handleApply = () => {
        if (dateRange.start && dateRange.end) fetchData(dateRange.start, dateRange.end, true);
    };

    const handleReset = () => {
        const def = defaultDateRangeRef.current;
        if (!def.start || !def.end) return;
        setDateRange(def);
        if (appliedDateRange.start !== def.start || appliedDateRange.end !== def.end) {
            setAppliedDateRange(def);
            fetchData();
        }
    };

    const handleExport = () => {
        if (!tableData?.rows?.length || loading || exportLoading) return;
        setExportLoading(true);
        Mixpanel.track(AICA_RECON_EXPORT_CLICKED, {
            userEmail: user?.userData?.primaryEmail || '',
            startDate: appliedDateRange.start,
            endDate: appliedDateRange.end,
            currentURL: window.location.href,
            pnoID: user?.userData?.investorOrgId || '',
        });
        downloadOperatingInflowReconExport(
            {
                orgId,
                gstType: 'REVENUE',
                ...(appliedDateRange.start && appliedDateRange.end
                    ? { startDate: appliedDateRange.start, endDate: appliedDateRange.end }
                    : {}),
            },
            {
                onSuccess: () => setExportLoading(false),
                onError: () => setExportLoading(false),
            },
        );
    };

    const pickerSharedProps = (isApplied: boolean) => ({
        placeholder: '',
        displayFormat: DATE_FORMATS.DATE_FORMAT_DDMMYYYY,
        responseFormat: DATE_FORMATS.ISO,
        className: styles.DatePickerInput,
        inputContainerClassName: styles.DateInputContainer,
        inputClassName: clsx(styles.DateInput, { [styles.DateInputApplied]: isApplied }),
        unitContainerClassName: clsx(styles.DateUnitContainer, { [styles.DateUnitContainerApplied]: isApplied }),
        unitClassName: styles.DateUnit,
        useFixedPosition: true,
        showYearNavigation: true,
        unit: <CalendarIcon className={styles.CalendarIcon} stroke={isApplied ? 'var(--primary-purple)' : '#4D4D4D'} strokeWidth={2} />,
    });

    const Loader = () => {
        return (
            <div style={{ background: 'white', padding: '16px', marginTop: '16px' }}>
                <React.Fragment>
                    <ShimmerThumbnail height={250} rounded className={styles.ShimmerBody} />
                </React.Fragment>
            </div>
        );
    };

    const renderTable = () => {
        if (loading) return <Loader />;

        if (!tableData?.rows?.length)
            return (
                <div className={styles.TableContainer}>
                    <div className={styles.NoDataCard}>
                        <div className={styles.Nodata}>
                            <div className={styles.NoDataContent}>
                                <img src={noDataFoundEmptyState} alt="No data" className={styles.NoDataImage} />
                                <div className={styles.NoDataTextRow}>
                                    <span>No data for selected duration. </span>
                                    <button
                                        type="button"
                                        className={styles.ResetToDefaultLink}
                                        onClick={handleReset}
                                        disabled={!defaultDateRangeRef.current.start || !defaultDateRangeRef.current.end || loading}
                                    >
                                        Reset to Default
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );

        return (
            <div className={styles.TableContainer}>
                <div className={styles.TableWrapper}>
                    <table className={styles.OperatingInflowTable}>
                        {!!tableData?.headers?.length && (
                            <thead className={styles.TableHead}>
                                <Header data={tableData?.headers} />
                            </thead>
                        )}
                        {!!tableData?.rows?.length && (
                            <tbody className={styles.TableBody}>
                                <Rows rows={tableData?.rows} headers={tableData?.headers} tableRef={null} assesseeOrgId={assesseeOrgId} />
                            </tbody>
                        )}
                    </table>
                </div>
            </div>
        );
    };

    const headerLeft = (
        <div className={styles.Left}>
            <span>Operating Inflow Reconciliation</span>
            {overlapDurationRanges.length > 0 && (
                <>
                    <span className={styles.HeaderDivider} />
                    <div className={styles.OverlapTooltipWrapper}>
                        <Tooltip
                            content={
                                <div className={styles.OverlapTooltipContent}>
                                    <div className={styles.OverlapTooltipTitle}>{OVERLAP_TOOLTIP_TITLE}</div>
                                    {overlapDurationRanges
                                        .flatMap((d) =>
                                            d
                                                .split(',')
                                                .map((s) => s.trim())
                                                .filter(Boolean),
                                        )
                                        .map((d) => (
                                            <div key={d} className={styles.OverlapTooltipRow}>
                                                <span className={styles.OverlapTooltipBullet}>•</span>
                                                <span>{d}</span>
                                            </div>
                                        ))}
                                </div>
                            }
                            direction="right"
                        >
                            <div className={styles.OverlapDurationBadge}>
                                <span className={styles.OverlapDurationText}>Overlap Duration</span>
                                <img className={styles.InfoIcon} src={infoToolTip} alt="overlap duration info" height="16px" />
                            </div>
                        </Tooltip>
                    </div>
                </>
            )}
        </div>
    );

    const headerRight = (
        <div className={styles.HeaderRightContainer}>
            <Button
                text={
                    <span className={styles.ExportButtonContent}>
                        {exportLoading ? (
                            <LoadingSpinner color="var(--primary-text-colour)" height="18px" style={{ margin: 0 }} />
                        ) : (
                            <img src={downloadIconDataRoomHovered} alt="Export" height="16px" />
                        )}
                        {exportLoading ? 'Exporting...' : 'Export'}
                    </span>
                }
                onClick={handleExport}
                type="button"
                disabled={!tableData?.rows?.length || loading || exportLoading}
                className={styles.ExportButton}
            />
            <div className={styles.TimePeriodContainer}>
                <span className={styles.TimePeriodLabel}>Time Period:</span>
                <DatePicker
                    value={dateRange.start}
                    onChange={(v) => setDateRange((prev) => ({ ...prev, start: v }))}
                    max={dateRange.end || undefined}
                    {...pickerSharedProps(isStartCustomApplied)}
                />
                <span className={styles.ToText}>to</span>
                <DatePicker
                    value={dateRange.end}
                    onChange={(v) => setDateRange((prev) => ({ ...prev, end: v }))}
                    min={dateRange.start || undefined}
                    {...pickerSharedProps(isEndCustomApplied)}
                />
                {hasUnappliedChanges && (
                    <Button
                        text="Apply"
                        onClick={handleApply}
                        variant="purple"
                        type="button"
                        loading={loading}
                        className={styles.ApplyButton}
                    />
                )}
                {isCustomFilterApplied && !hasUnappliedChanges && (
                    <Button
                        text="Reset"
                        onClick={handleReset}
                        variant="secondary"
                        type="button"
                        loading={loading}
                        className={styles.ResetButton}
                    />
                )}
            </div>
        </div>
    );

    return (
        <div className={styles.OperatingInflowReconciliation}>
            <HeaderBodyBox title={headerLeft} headerRight={headerRight} headerClassName={styles.ReconciliationHeader}>
                <div className={styles.TableContainerWrapper}>{renderTable()}</div>
            </HeaderBodyBox>
        </div>
    );
}

export default OperatingInflowReconciliation;
